import { useCallback, useEffect, useMemo, useState } from 'react'
import feedbackRequest from 'src/api/requests/feedback'
import { MESSAGE_TYPE } from 'src/containers/Chat/constants'
import { PlanDataInterface } from 'src/interfaces/plan'
import { useAppDispatch } from 'src/store'
import { getUserTreatmentPlan } from 'src/useCase/userTreatment'

export const usePlanMessageData = (
  type_message: number,
  message_content: string,
) => {
  const dispatch = useAppDispatch()
  const [selectedPlan, setSelectedPlan] = useState<PlanDataInterface | null>(
    null,
  )
  const [feedbackData, setFeedbackData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [changedPlanId, setChangedPlanId] = useState<number | null>(null)

  useEffect(() => {
    if (type_message === MESSAGE_TYPE.PLAN && message_content) {
      try {
        const data = JSON.parse(message_content)
        const feedbackId = data?.doctor_feedback_id
        const treatmentId = data?.user_treatment_id

        if (!feedbackId && !treatmentId) return

        setIsLoading(true)

        const promises: Promise<any>[] = []

        if (feedbackId) {
          promises.push(
            feedbackRequest
              .getFeedbackDetail(feedbackId, {
                with: 'plans.medicines,doctor',
                from: 'message',
              })
              .then((res) => setFeedbackData(res.data)),
          )
        }

        if (treatmentId) {
          promises.push(
            dispatch(
              getUserTreatmentPlan({
                id: treatmentId,
                params: { with: 'plan' },
              }),
            )
              .unwrap()
              .then((plan) => setChangedPlanId(plan?.id ?? null))
              .catch(() => setChangedPlanId(null)),
          )
        }

        Promise.all(promises).finally(() => setIsLoading(false))
      } catch {
        return
      }
    }
  }, [type_message, message_content])

  useEffect(() => {
    if (feedbackData?.plans?.length) {
      const recommended = feedbackData.plans.find(
        (plan) => plan.pivot?.recommended === true,
      )
      if (recommended && !selectedPlan) {
        setSelectedPlan(recommended)
      }
    }
  }, [feedbackData, selectedPlan])

  const mapPlanData = useCallback(
    (plan: any): PlanDataInterface => ({
      ...plan,
      medicines: plan.medicines || [],
      tags: plan.tags || [],
      treatment_policy: plan.treatment_policy || '',
    }),
    [],
  )

  const changedPlan = useMemo(() => {
    if (changedPlanId == null) return null
    const plan = feedbackData?.plans?.find((p: any) => p.id === changedPlanId)
    return plan ? mapPlanData(plan) : null
  }, [feedbackData, changedPlanId, mapPlanData])

  const planImportants = useMemo(() => {
    // Only exclude changedPlanId when changedPlan was actually found in feedbackData
    // (i.e. it will be rendered separately above). If changedPlan is null the plan
    // is not in this feedback's list, so keep old behaviour — let it show here.
    const shouldExclude = changedPlan !== null
    const importantFromFeedbacks =
      feedbackData?.plans?.filter(
        (plan) =>
          plan.pivot?.recommended &&
          (!shouldExclude || plan.id !== changedPlanId),
      ) ?? []

    const allImportant = [...importantFromFeedbacks]

    if (
      selectedPlan?.recommended &&
      (!shouldExclude || selectedPlan.id !== changedPlanId) &&
      !allImportant.some((p) => p.id === selectedPlan.id)
    ) {
      allImportant.push(selectedPlan)
    }

    return allImportant.map(mapPlanData)
  }, [feedbackData, selectedPlan, changedPlanId, changedPlan, mapPlanData])

  const planContents = useMemo(() => {
    const shouldExclude = changedPlan !== null
    const fbPlans = feedbackData?.plans?.map(mapPlanData) || []
    if (!selectedPlan) {
      const filtered = fbPlans.filter(
        (plan) =>
          !planImportants.some((imp) => imp.id === plan.id) &&
          (!shouldExclude || plan.id !== changedPlanId),
      )
      return [
        ...filtered.filter((p) => p.selected_from_lp),
        ...filtered.filter((p) => !p.selected_from_lp),
      ]
    }

    const exists = fbPlans.some((p) => p.id === selectedPlan.id)
    const merged = exists ? fbPlans : [...fbPlans, mapPlanData(selectedPlan)]
    const filtered = merged.filter(
      (plan) =>
        !planImportants.some((imp) => imp.id === plan.id) &&
        (!shouldExclude || plan.id !== changedPlanId),
    )
    return [
      ...filtered.filter((p) => p.selected_from_lp),
      ...filtered.filter((p) => !p.selected_from_lp),
    ]
  }, [
    feedbackData,
    selectedPlan,
    planImportants,
    changedPlanId,
    changedPlan,
    mapPlanData,
  ])

  return {
    planContents,
    planImportants,
    changedPlan,
    selectedPlan,
    feedbackData,
    isLoading,
    changedPlanId,
  }
}
